import { LottoResult, UserChoice } from '../types';
import { api } from './ApiClient';
import axios from 'axios';

// 이번 주 로또 정보 조회 API
export const getThisWeekPrize = async (guestToken: string, signal?: AbortSignal): Promise<LottoResult> => {
  try {
    const response = await api.post<LottoResult>(
      '/api/lotto/GetThisWeekPrize',
      null,
      guestToken,
      { signal }
    );
    
    if (!response.success) {
      throw new Error(response.message || '로또 정보 조회에 실패했습니다.');
    }
    
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    // 이번 주 로또 정보 조회 실패 - 현재 회차 정보 로드 문제 진단을 위해 로그 유지
    console.error('이번 주 로또 정보 조회 실패:', error);
    throw error;
  }
};

// 특정 회차 로또 정보 조회 API
export const getPrizeBySequenceNo = async (
  guestToken: string, 
  sequenceNo: number,
  signal?: AbortSignal
): Promise<LottoResult> => {
  try {
    // 회차 번호 유효성 검사
    if (!sequenceNo || sequenceNo <= 0) {
      throw new Error('유효하지 않은 회차 번호입니다.');
    }
    
    const response = await api.post<LottoResult>(
      '/api/lotto/GetPrizeBySeqNo',
      { sequence_no: sequenceNo },
      guestToken,
      { signal }
    );
    
    if (!response.success) {
      throw new Error(response.message || '로또 정보 조회에 실패했습니다.');
    }
    
    return response;
  } catch (error) {
    // axios의 CanceledError는 상위로 전파
    if (axios.isCancel(error)) {
      throw error;
    }
    // 특정 회차 로또 정보 조회 실패 - 회차별 정보 로드 문제 진단을 위해 로그 유지
    console.error('특정 회차 로또 정보 조회 실패:', error);
    throw error;
  }
};

// 사용자 선택 번호 저장 API
export const saveUserChoice = async (
  token: string,
  numbers: number[]
): Promise<void> => {
  try {
    if (numbers.length !== 6) {
      throw new Error('6개의 번호를 선택해야 합니다.');
    }
    
    const data = {
      digit1: numbers[0],
      digit2: numbers[1],
      digit3: numbers[2],
      digit4: numbers[3],
      digit5: numbers[4],
      digit6: numbers[5]
    };
    
    const response = await api.post<{ success: boolean; message: string }>(
      '/api/lotto/SaveUserChoice',
      data,
      token
    );
    
    if (!response.success) {
      throw new Error(response.message || '번호 저장에 실패했습니다.');
    }
  } catch (error) {
    // 사용자 선택 번호 저장 실패 - 번호 저장 문제 진단을 위해 로그 유지
    console.error('사용자 선택 번호 저장 실패:', error);
    throw error;
  }
};

// 사용자 선택 번호 조회 API
export const getUserChoices = async (
  token: string,
  sequenceNo?: number,
  signal?: AbortSignal
): Promise<UserChoice[]> => {
  try {
    const data = sequenceNo ? { sequence_no: sequenceNo } : {};
    
    const response = await api.post<{ success: boolean; message: string; result: UserChoice[] }>(
      '/api/lotto/GetUserChoices',
      data,
      token,
      { signal }
    );
    
    return response.result || [];
  } catch (error) {
    // axios의 CanceledError는 상위로 전파
    if (axios.isCancel(error)) {
      throw error;
    }
    // 사용자 선택 번호 조회 실패 - 저장된 번호 로드 문제 진단을 위해 로그 유지
    console.error('사용자 선택 번호 조회 실패:', error);
    throw error;
  }
};

// 사용자 선택 번호 삭제 API
export const deleteUserChoice = async (
  token: string,
  choiceNo: string
): Promise<void> => {
  try {
    const response = await api.post<{ success: boolean; message: string }>(
      '/api/lotto/DeleteUserChoice',
      { choiceNo },
      token
    );
    
    if (!response.success) {
      throw new Error(response.message || '번호 삭제에 실패했습니다.');
    }
  } catch (error) {
    // 사용자 선택 번호 삭제 실패 - 번호 삭제 문제 진단을 위해 로그 유지
    console.error('사용자 선택 번호 삭제 실패:', error);
    throw error;
  }
};

// 사용자 선택 번호 재발송 API
// keepExisting: true  = 이전 조합 그대로 재발송(구매 후 당첨확인 일치)
//               false = 새 조합으로 재조합하여 발송(기본)
export const sendChoicedNumbers = async (
  token: string,
  sequenceNo: number,
  keepExisting: boolean = false
): Promise<void> => {
  try {
    const response = await api.post<{ success: boolean; message: string }>(
      `/api/lotto/SendChoicedNumbers?sequence_no=${sequenceNo}&keep_existing=${keepExisting}`,
      null,
      token
    );
    
    if (!response.success) {
      throw new Error(response.message || '선택한 번호 재발송에 실패했습니다.');
    }
  } catch (error) {
    // 선택한 번호 재발송 실패 - 이메일 발송 문제 진단을 위해 로그 유지
    console.error('선택한 번호 재발송 실패:', error);
    throw error;
  }
};

// 로또 서비스 객체
export const lottoService = {
  getThisWeekPrize,
  getPrizeBySequenceNo,
  saveUserChoice,
  getUserChoices,
  deleteUserChoice,
  sendChoicedNumbers
}; 
